import PageHero from "@/components/site/PageHero";
import CTAStrip from "@/components/site/CTAStrip";
import RelatedLinks from "@/components/site/RelatedLinks";
import {
  AnswerBlock,
  ProcessStep,
  Breadcrumb,
  Prose,
  ReviewStamp,
} from "@/components/site/ContentPrimitives";
import ProcessFlow from "@/components/diagrams/ProcessFlow";
import AnnotatedLabel from "@/components/diagrams/AnnotatedLabel";
import {
  useSEO,
  useJsonLd,
  SITE_URL,
  breadcrumbSchema,
  articleSchema,
} from "@/lib/seo";
import { linkifyGoAndTrack } from "@/lib/goAndTrack";

export default function HowDisposableTrackersWork() {
  useSEO({
    title: "How Disposable Package Trackers Work | Step-by-Step",
    description:
      "How disposable package trackers work — activation, application, dispatch, transmission, monitoring, and end-of-journey — with technology and sensor detail.",
    canonical: "/how-disposable-trackers-work",
    image: `${SITE_URL}/images/ll100-peel.jpg`,
  });

  useJsonLd(
    "breadcrumb-hdw",
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Disposable Package Tracker", url: "/disposable-package-tracker" },
      { name: "How Disposable Trackers Work" },
    ]),
  );

  useJsonLd(
    "article-hdw",
    articleSchema({
      headline: "How Disposable Package Trackers Work",
      description:
        "Step-by-step explanation of how disposable smart labels move through one shipment — activation to end-of-journey.",
      path: "/how-disposable-trackers-work",
      image: `${SITE_URL}/images/ll100-peel.jpg`,
      datePublished: "2026-02-01",
    }),
  );

  return (
    <div>
      <PageHero
        eyebrow="How it works"
        title="How Disposable Package Trackers Work"
        subtitle="A step-by-step walk through what happens inside a single-use tracking device — from the moment you pull the activation tab to the last data point after delivery."
      />

      <section className="section">
        <div className="container-x">
          <Breadcrumb
            trail={[
              { label: "Home", href: "/" },
              { label: "Disposable Package Tracker", href: "/disposable-package-tracker" },
              { label: "How Disposable Trackers Work" },
            ]}
          />

          <Prose>
            <h2 className="h2">The seven-step lifecycle</h2>
            <AnswerBlock>
              A disposable tracker moves through activation, attachment,
              dispatch, detection, transmission, monitoring, and completion
              — all inside a single shipment. It is not recovered.
            </AnswerBlock>
            <ProcessFlow />

            <h2 className="h2 mt-16">Inside the label</h2>
            <p className="body-text mt-3">
              A disposable smart label is a thin sandwich of an adhesive
              substrate, a printed antenna, a low-power microcontroller
              with a radio module, and a non-replaceable battery. On
              cellular / GNSS variants, an additional GNSS receiver and
              condition sensors are included.
            </p>
            <AnnotatedLabel />

            <h2 className="h2 mt-16">Step-by-step in detail</h2>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <ProcessStep n={1} title="Activate">
                Pulling the activation tab breaks the circuit gap, powers
                the MCU, and starts the reporting schedule configured for
                the shipment. No pairing or firmware setup on the shipper&apos;s
                floor.
              </ProcessStep>
              <ProcessStep n={2} title="Attach">
                The label is applied directly to the parcel, carton, tote,
                or pallet. The peel-and-stick backing does not require a
                housing.
              </ProcessStep>
              <ProcessStep n={3} title="Dispatch">
                The shipment enters the lane. The device is already
                sampling — no dispatch-site infrastructure is required.
              </ProcessStep>
              <ProcessStep n={4} title="Detect">
                On BLE labels, the radio advertises on a schedule and is
                seen by any nearby gateway. On cellular / GNSS labels,
                position is fixed from satellites and augmented from
                cell/Wi-Fi where useful. Condition sensors sample at the
                same time.
              </ProcessStep>
              <ProcessStep n={5} title="Transmit">
                BLE devices push data through the gateway that saw them;
                cellular devices open a data session directly to the
                network. Both terminate at the {linkifyGoAndTrack("GoAndTrack")}{" "}
                platform.
              </ProcessStep>
              <ProcessStep n={6} title="Monitor">
                Operations sees live location, dwell, condition
                exceptions, and lane-level analytics — the shipper can act
                on exceptions long before delivery.
              </ProcessStep>
              <ProcessStep n={7} title="Complete + dispose">
                At end-of-journey the device is not recovered. Disposal
                follows the applicable e-waste and battery-waste route for
                the specific device.
              </ProcessStep>
            </div>

            <h2 className="h2 mt-16">Reporting behaviour</h2>
            <AnswerBlock>
              Reporting cadence is a trade-off between visibility and
              battery. Every additional position or sensor sample costs
              energy, so devices are configured against the shipment
              profile — long slow lanes report less frequently, sensitive
              short lanes report often.
            </AnswerBlock>
            <ul className="mt-4 space-y-2 body-text list-disc pl-6">
              <li>
                Motion-aware sampling: report more often when moving, less
                when dwelling.
              </li>
              <li>
                Exception-triggered sampling: a shock, temperature excursion,
                or geofence event forces an immediate report.
              </li>
              <li>
                Scheduled reporting: fixed intervals — the simplest baseline.
              </li>
            </ul>

            <h2 className="h2 mt-16">Data delivered</h2>
            <p className="body-text mt-3">
              The platform receives, at minimum: device ID, timestamp,
              position, and battery. Devices with sensors add temperature,
              humidity, shock, ambient light, and door / seal events.{" "}
              {linkifyGoAndTrack("GoAndTrack")} exposes this as live views,
              lane analytics, geofence alerts, exception queues, and
              webhook / API feeds into TMS, WMS, and ERP systems.
            </p>

            <ReviewStamp
              date="February 2026"
              basis="Manufacturer specifications for the deployed BLE and cellular/GNSS label classes and direct deployment experience across UK/EU logistics lanes."
            />
          </Prose>
        </div>
      </section>

      <RelatedLinks
        links={[
          { to: "/disposable-package-tracker", label: "Disposable Package Tracker — complete category guide" },
          { to: "/gps-vs-ble-vs-cellular", label: "GPS vs BLE vs Cellular — technology comparison" },
          { to: "/bluetooth-smart-labels", label: "Bluetooth Smart Labels — BLE technology in detail" },
          { to: "/disposable-gps-tracker", label: "Disposable GPS Tracker — GNSS-class devices" },
        ]}
      />

      <CTAStrip
        title="Ready to see it working on your lane?"
        body="Speak to a specialist about a structured pilot — real devices, real lanes, KPI reviewed at 30 days."
      />
    </div>
  );
}
